/**
 * 라이브러리 학습지 정답지 — 개요에서 정답·해설 쪽을 그린다.
 *
 * 1순위: 저장된 캔버스의 teacher_guide 쪽, 또는 학생용과 같은
 * `buildWorksheetCanvas({ includeAnswerGuide: true })` 페이지네이션.
 * 2순위: 같은 A4(794×1123) 패킹으로 로컬 쪽을 만든다.
 * 제목은 항상 「정답 및 해설」. 시험지형(2단)도 정답지는 1단.
 */

import { fetchWorksheetCanvasOutline } from './worksheetCanvasFetch';
import { getTheme, DEFAULT_THEME_ID } from './canvasThemes';
import {
  applyPaperFormatToOutline,
  paperFormatFromOutline,
} from './worksheetPaperFormat';

export const ANSWER_KEY_TITLE = '정답 및 해설';
export const ANSWER_KEY_PAGE_W = 794;
export const ANSWER_KEY_PAGE_H = 1123;

const MARGIN_X = 48;
const MARGIN_TOP = 40;
const MARGIN_BOTTOM = 56;
const HEADER_H = 88;
const MAX_PAGES = 30;
const BACKEND_TITLE = '교사용 정답·해설';

const asText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (Array.isArray(value)) {
    return value.map(asText).filter(Boolean).join('\n');
  }
  if (typeof value === 'object') {
    if (typeof value.text === 'string') return value.text.trim();
    return Object.values(value).map(asText).filter(Boolean).join('\n');
  }
  return '';
};

const asPoints = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const keyPointsOf = (guide) => {
  const raw = guide?.key_points || guide?.teaching_points || guide?.rubric_points || [];
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  return list.map(asText).filter(Boolean);
};

export function outlineHasStoredAnswers(outline) {
  return collectAnswerKeyEntries(outline).length > 0;
}

export function collectAnswerKeyEntries(outline) {
  if (!outline || typeof outline !== 'object') return [];
  const sections = Array.isArray(outline.sections) ? outline.sections : [];
  if (sections.length) {
    const entries = [];
    sections.forEach((section, sectionIndex) => {
      if (!section || typeof section !== 'object') return;
      const sectionNumber = section.section_number || sectionIndex + 1;
      const sectionTitle = asText(section.title) || `${sectionNumber}번 활동`;
      const items = Array.isArray(section.content_items) ? section.content_items : [];
      items.forEach((item, itemIndex) => {
        if (!item || typeof item !== 'object') return;
        const guide = item.teacher_guide && typeof item.teacher_guide === 'object'
          ? item.teacher_guide
          : {};
        const answer = asText(guide.answer || guide.model_answer || item.answer);
        const explanation = asText(guide.explanation || guide.rationale || item.explanation);
        const keyPoints = keyPointsOf(guide);
        if (!answer && !explanation && !keyPoints.length) return;
        entries.push({
          displayNumber: `${sectionNumber}-${itemIndex + 1}`,
          prompt: asText(item.question || item.prompt || item.title) || sectionTitle,
          answer,
          explanation: explanation || keyPoints.join('\n'),
          points: asPoints(item.points || guide.points),
        });
      });
      const tip = asText(section.teacher_tip);
      if (tip) {
        entries.push({
          displayNumber: String(sectionNumber),
          prompt: '수업 운영 참고',
          answer: '',
          explanation: tip,
          points: 0,
        });
      }
    });
    return entries;
  }

  const questions = Array.isArray(outline.questions) ? outline.questions : [];
  return questions.reduce((entries, question, index) => {
    if (!question || typeof question !== 'object') return entries;
    const answer = asText(question.answer);
    const explanation = asText(question.explanation);
    if (!answer && !explanation) return entries;
    entries.push({
      displayNumber: String(question.number || index + 1),
      prompt: asText(question.question || question.title) || `${index + 1}번`,
      answer,
      explanation,
      points: asPoints(question.points),
    });
    return entries;
  }, []);
}

export function teacherGuideSlides(outlineOrSlides) {
  const slides = Array.isArray(outlineOrSlides)
    ? outlineOrSlides
    : outlineOrSlides?.slides;
  if (!Array.isArray(slides)) return [];
  return slides.filter((slide) => slide?.page_kind === 'teacher_guide' && slide?.canvas);
}

const remapAnswerKeyTitle = (slides) => slides.map((slide) => {
  const objects = slide?.canvas?.objects;
  if (!Array.isArray(objects)) return slide;
  let changed = false;
  const next = objects.map((obj) => {
    const role = obj?.semantic?.role;
    const isTitle = role === 'teacher-guide-title' || obj?.text === BACKEND_TITLE;
    if (!isTitle || obj.text === ANSWER_KEY_TITLE) return obj;
    changed = true;
    return { ...obj, text: ANSWER_KEY_TITLE };
  });
  if (!changed) return slide;
  return { ...slide, canvas: { ...slide.canvas, objects: next } };
});

const estimateHeight = (text, fontSize, width, lineHeight = 1.45) => {
  const charsPerLine = Math.max(8, Math.floor(width / (fontSize * 0.58)));
  const lines = String(text || '').split('\n').reduce((sum, line) => (
    sum + Math.max(1, Math.ceil((line || ' ').length / charsPerLine))
  ), 0);
  return Math.max(fontSize * lineHeight, lines * fontSize * lineHeight);
};

const textbox = (id, text, x, y, w, h, style, role, z = 2) => ({
  id,
  type: 'textbox',
  x,
  y,
  w,
  h,
  rotate: 0,
  z,
  text,
  style,
  semantic: { role },
});

const rect = (id, x, y, w, h, style, role) => ({
  id,
  type: 'shape',
  shape: 'rect',
  x,
  y,
  w,
  h,
  rotate: 0,
  z: 1,
  style,
  semantic: { role },
});

const buildHeader = (title, theme, idPrefix, contentW) => {
  const c = theme.colors;
  const x = MARGIN_X;
  const y = MARGIN_TOP;
  return {
    objects: [
      rect(`${idPrefix}_hdr`, x, y, contentW, HEADER_H, {
        fill: c.accentSoft,
        borderRadius: 10,
      }, 'teacher-guide-header'),
      textbox(`${idPrefix}_title`, ANSWER_KEY_TITLE, x + 18, y + 14, contentW - 36, 24, {
        fontSize: 22,
        fontWeight: 800,
        color: c.accent,
        fontFamily: 'Pretendard, system-ui, sans-serif',
      }, 'teacher-guide-title'),
      textbox(`${idPrefix}_ws`, title || '학습지', x + 18, y + 50, contentW - 36, 18, {
        fontSize: 13,
        fontWeight: 550,
        color: c.text2,
        fontFamily: 'Pretendard, system-ui, sans-serif',
      }, 'teacher-guide-worksheet-title'),
    ],
    nextY: y + HEADER_H + 18,
  };
};

const buildEntryBlocks = (entry, theme, idPrefix, contentW) => {
  const c = theme.colors;
  const blocks = [];
  const pushField = (label, body, variant) => {
    if (!body) return;
    const bodySize = variant === 'answer' ? 15 : 14;
    const bodyH = estimateHeight(body, bodySize, contentW - 28);
    const blockH = Math.round(44 + bodyH + 14);
    const fill = variant === 'prompt' ? c.accentSoft : c.bg;
    const labelColor = variant === 'answer' || variant === 'prompt' ? c.accent : c.text2;
    blocks.push({
      height: blockH,
      objects: [
        rect(`${idPrefix}_${variant}_card`, 0, 0, contentW, blockH, {
          fill,
          stroke: c.border,
          strokeWidth: 1,
          borderRadius: 8,
        }, 'teacher-guide-card'),
        textbox(`${idPrefix}_${variant}_label`, label, 14, 10, contentW - 28, 16, {
          fontSize: 13,
          fontWeight: 750,
          color: labelColor,
          fontFamily: 'Pretendard, system-ui, sans-serif',
        }, 'teacher-guide-label'),
        textbox(`${idPrefix}_${variant}_body`, body, 14, 32, contentW - 28, bodyH, {
          fontSize: bodySize,
          fontWeight: variant === 'answer' ? 650 : 450,
          color: c.text1,
          lineHeight: 1.45,
          fontFamily: 'Pretendard, system-ui, sans-serif',
        }, `teacher-guide-${variant}`),
      ],
    });
  };

  pushField(`${entry.displayNumber}번`, entry.prompt, 'prompt');
  if (entry.answer) pushField('정답', entry.answer, 'answer');
  if (entry.explanation) pushField('해설', entry.explanation, 'explanation');
  if (entry.points) pushField('배점', `${entry.points}점`, 'points');
  return blocks;
};

const placeRelative = (objects, dx, dy, idSuffix) => objects.map((obj, index) => ({
  ...obj,
  id: `${obj.id}_${idSuffix}_${index}`,
  x: Math.round((obj.x + dx) * 10) / 10,
  y: Math.round((obj.y + dy) * 10) / 10,
}));

export function paginateAnswerKeyLocal(outline, entries, themeId) {
  const theme = getTheme(themeId || outline?.canvas_theme || DEFAULT_THEME_ID);
  const title = asText(outline?.title) || '학습지';
  const contentW = ANSWER_KEY_PAGE_W - MARGIN_X * 2;
  const bottom = ANSWER_KEY_PAGE_H - MARGIN_BOTTOM;
  const pages = [];
  let objects = [];
  let cursor = 0;
  let pageHasContent = false;

  const startPage = () => {
    const header = buildHeader(title, theme, `ak${pages.length + 1}`, contentW);
    objects = header.objects;
    cursor = header.nextY;
    pageHasContent = false;
  };

  const flushPage = () => {
    if (!objects.length) return;
    pages.push({
      slide_id: `worksheet_teacher_guide_${pages.length + 1}`,
      slide_number: pages.length + 1,
      section_page_number: pages.length + 1,
      page_kind: 'teacher_guide',
      title: ANSWER_KEY_TITLE,
      canvas: {
        width: ANSWER_KEY_PAGE_W,
        height: ANSWER_KEY_PAGE_H,
        background: theme.colors.bg,
        objects,
      },
    });
    objects = [];
  };

  startPage();
  const maxContent = bottom - cursor;

  entries.forEach((entry, entryIndex) => {
    if (pages.length >= MAX_PAGES) return;
    const blocks = buildEntryBlocks(entry, theme, `e${entryIndex}`, contentW);
    const entryH = blocks.reduce((sum, block) => sum + block.height + 10, 0) + 8;
    if (entryH <= maxContent && entryH > (bottom - cursor) && pageHasContent) {
      flushPage();
      if (pages.length >= MAX_PAGES) return;
      startPage();
    }
    blocks.forEach((block, blockIndex) => {
      if (block.height > (bottom - cursor) && pageHasContent) {
        flushPage();
        if (pages.length >= MAX_PAGES) return;
        startPage();
      }
      objects.push(...placeRelative(block.objects, MARGIN_X, cursor, `${entryIndex}_${blockIndex}`));
      cursor += block.height + (blockIndex === blocks.length - 1 ? 18 : 10);
      pageHasContent = true;
    });
  });
  flushPage();

  const total = pages.length;
  pages.forEach((page, index) => {
    page.canvas.objects.push(textbox(
      `ak_foot_${index + 1}`,
      `정답·해설 ${index + 1} / ${total}`,
      MARGIN_X,
      ANSWER_KEY_PAGE_H - MARGIN_BOTTOM + 16,
      contentW,
      16,
      {
        fontSize: 12,
        fontWeight: 500,
        color: theme.colors.text3,
        textAlign: 'center',
        fontFamily: 'Pretendard, system-ui, sans-serif',
      },
      'teacher-guide-pagenum',
    ));
  });
  return pages;
}

export async function buildAnswerKeyCanvas({ outline, themeId, signal } = {}) {
  if (!outline || typeof outline !== 'object') {
    return { success: false, slides: [], outline: null };
  }
  const existing = remapAnswerKeyTitle(teacherGuideSlides(outline));
  if (existing.length) {
    return { success: true, slides: existing, outline };
  }
  const entries = collectAnswerKeyEntries(outline);
  if (!entries.length) {
    return { success: false, slides: [], outline };
  }

  if (typeof fetchWorksheetCanvasOutline === 'function') {
    try {
      const format = paperFormatFromOutline(outline);
      const outlineForBuild = applyPaperFormatToOutline(outline, format);
      const result = await fetchWorksheetCanvasOutline({
        outline: outlineForBuild,
        includeAnswerGuide: true,
        worksheetStyle: outlineForBuild.worksheet_style,
        theme: themeId || outline.canvas_theme,
        signal,
      });
      const fromApi = result?.success
        ? remapAnswerKeyTitle(teacherGuideSlides(result.outline))
        : [];
      if (fromApi.length) {
        return { success: true, slides: fromApi, outline: result.outline };
      }
    } catch (_error) {
      // 로컬 패킹으로 이어간다.
    }
  }

  const slides = paginateAnswerKeyLocal(outline, entries, themeId);
  return {
    success: slides.length > 0,
    slides,
    outline: { ...outline, slides, answer_guide_page_count: slides.length },
  };
}
